import React, { useState, useEffect } from 'react';
import './App.css';
import ProjectsCarousel from './components/ProjectsCarousel';

// Logo component
const Logo = () => (
  <div className="logo">
    <div className="logo-dog">
             <img src="/assets/logo.jpg" alt="The Golden Toolbelt Logo" className="dog-svg" />
    </div>
  </div>
);

// Navigation component
const Navigation = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMenuOpen(false);
  };

  return (
    <nav className="navigation">
      <div className="nav-container">
        <div className="nav-logo">
          <Logo />
        </div>
        <div className={`nav-menu ${isMenuOpen ? 'nav-menu-open' : ''}`}>
          <button onClick={() => scrollToSection('about')} className="nav-link">About</button>
          <button onClick={() => scrollToSection('services')} className="nav-link">Services</button>
          <button onClick={() => scrollToSection('work')} className="nav-link">Our Work</button>
          <button onClick={() => scrollToSection('contact')} className="nav-link">Contact</button>
        </div>
        <button 
          className="nav-toggle"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
      </div>
    </nav>
  );
};

// Hero section
const Hero = () => (
  <section className="hero">
    <div className="hero-content">
      <div className="hero-logo">
        <Logo />
      </div>
      <h1 className="hero-title">Jack-of-All-Trades Services</h1>
      <p className="hero-subtitle">From Traditional Handyman Work to Custom Furniture, Fabrication, & Beyond</p>
      <button 
        className="hero-cta"
        onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
      >
        Get Free Quote
      </button>
    </div>
  </section>
);

// About section
const About = () => (
  <section id="about" className="about">
    <div className="container">
      <h2 className="section-title">About The Golden Toolbelt</h2>
      <div className="about-content">
        <div className="about-text">
          <p>
          With more than a quarter century of experience in software engineering, I’ve spent most of my professional life solving complex problems and honing a meticulous attention to detail. While my career in tech has been rewarding, it has always been balanced by a set of creative hobbies that fueled a different kind of passion. The projects you see in my photos—custom furniture, metalwork, lighted signs, home improvements—represent years of self‑taught craftsmanship and a deep love for making things with my hands. I especially enjoy brainstorming ideas for new pieces or repairs, transforming a sketch or loose concept into a practical design.
          </p>
          <p>
          After over 25 years in the world of software, I’ve decided it’s time for a change. I’m stepping away from screens and code to embrace the skills I’ve developed outside my day job. This new venture is more than a business—it’s an opportunity to do what I love every day, blending technical precision with artisanal creativity. I bring an engineering mindset to every project, whether I’m designing bespoke furniture, restoring vintage machines, or crafting personalized décor; careful planning and problem‑solving are at the core of my approach. I’m excited to share this new chapter with you and turn my passions into services that make a difference in your home or business.
          </p>
          <div className="about-features">
          <div className="feature">
              <h4>Handyman Services, Custom Furniture, Metalwork, and More</h4>
              <p>We offer a wide range of services, from traditional home repairs to custom furniture building and metalwork.</p>
            </div>
            <div className="feature">
              <h4>Licensed & Insured</h4>
              <p>Fully licensed and insured for your peace of mind</p>
            </div>
            <div className="feature">
              <h4>Traditional & Modern Methods</h4>
              <p>Combining classic craftsmanship with cutting-edge technology</p>
            </div>
            <div className="feature">
              <h4>Custom Solutions</h4>
              <p>Every project is tailored to your unique specifications and style</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);



// Services section
const HandymanServices = () => {  
  const services = [
    {
      title: "Home Repairs",
      description: "We handle all your home repair needs, inside and out.",
      icon: "🔧"
    },
    {
      title: "Smart Home Automation",
      description: "If you want to make your home more efficient and secure, we can help you with that.",
      icon: "🏠"
    },
    {
      title: "Outdoor Structures, Gates, Metalwork, and More",
      description: "Construct and install outdoor living features, including decks, pergolas, fences, and sliding driveway gates. Services include structural design, fabrication of metal and wood components, and seamless integration into existing landscapes.",
      icon: "🏗️"
    },
    {
      title: "Appliance Repair Diagnostics and Repairs",
      description: "We can diagnose and repair your appliances, from washing machines to refrigerators to ovens.",
      icon: "🚰"
    },
    {
      title: "Other Services",
      description: "Just ask, we can probably help you with that.",
      icon: "🤷"
    }
  ];

  return (
    <section id="services" className="services">
      <div className="container">
        <h2 className="section-title">Handyman Services Offered</h2>
        <div className="services-grid">
          {services.map((service, index) => (
            <div key={index} className="service-card">
              <div className="service-icon">{service.icon}</div>
              <h3 className="service-title">{service.title}</h3>
              <p className="service-description">{service.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};


  
const CustomFabricationServices = () => {
  const services = [
    {
      title: "Custom Furniture & Carpentry",
      description: "Design and build bespoke pieces such as dining tables, benches, outdoor furniture, and more. Each piece is tailored to clients' dimensions and style preferences, using high‑quality hardwoods, metal frames, and durable finishes.",
      icon: "🪚"
    },
    {
      title: "Outdoor Structures, Gates, Metalwork, and More",
      description: "Construct and install outdoor living features, including decks, pergolas, fences, and sliding driveway gates. Services include structural design, fabrication of metal and wood components, and seamless integration into existing landscapes.",
      icon: "🏗️"
    },
    {
      title: "Personalized Signs & Décor",
      description: "Create custom wooden and acrylic signs with engraved or illuminated lettering. Options range from name plaques and cutting boards to LED \"on air\" signs, infinity mirrors, and creative logos for home or business display.",
      icon: "🪧"
    },
    {
      title: "Digital Fabrication & Prototyping",
      description: "Offer CNC routing and 3D printing to produce precise parts, prototypes, or decorative items. Clients can bring designs or collaborate on custom patterns and intricate details, ideal for one‑off pieces or small‑batch production.",
      icon: "🖨️"
    },
    {
      title: "Mechanical Restoration & Customization",
      description: "Provide mechanical repair, restoration, and customization services for vintage vehicles or specialty equipment. This includes disassembly, component refurbishment, and reassembly to bring classic machines back to life.",
      icon: "⚙️"
    }
  ];


  return (
    <section id="custom-fabrication-services" className="services">
      <div className="container">
        <h2 className="section-title">Custom Fabrication Services Offered</h2>
        <p className="section-subtitle">Let us make something custom for you!</p>
        <div className="services-grid">
          {services.map((service, index) => (
            <div key={index} className="service-card">
              <div className="service-icon">{service.icon}</div>
              <h3 className="service-title">{service.title}</h3>
              <p className="service-description">{service.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Our Work section
const OurWork = () => {
  return (
    <section id="work" className="work">
      <div className="container">
        <h2 className="section-title">Our Work</h2>
        <p className="section-subtitle">Take a look at some of our recent projects</p>
        {/* Carousel of images from public/assets/projects */}
        <ProjectsCarousel />
      </div>
    </section>
  );
};

// Contact section
const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Handle form submission here
    alert('Thank you for your message! We\'ll get back to you soon.');
    setFormData({ name: '', email: '', phone: '', message: '' });
  };

  return (
    <section id="contact" className="contact">
      <div className="container">
        <h2 className="section-title">Contact Us</h2>
        <div className="contact-content">
          <div className="contact-info">
            <h3>Get In Touch</h3>
            <p>Ready to start your next project? Contact us today for a free estimate!</p>
            
            {/* <div className="contact-item">
              <strong>📞 Phone:</strong>
              <span>(512)537-4907</span>
            </div> */}
            
            <div className="contact-item">
              <strong>📧 Email:</strong>
              <span>info@thegoldentoolbelt.com</span>
            </div>
            
            <div className="contact-item">
              <strong>📍 Service Area:</strong>
              <span>Central Austin and Surrounding Areas</span>
            </div>
            
            <div className="contact-item">
              <strong>🕒 Hours:</strong>
              <span>Monday - Friday: 10AM - 5PM</span>
            </div>
          </div>
          
        {/*   <form className="contact-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <input
                type="text"
                name="name"
                placeholder="Your Name"
                value={formData.name}
                onChange={handleInputChange}
                required
              />
            </div>
            
            <div className="form-group">
              <input
                type="email"
                name="email"
                placeholder="Your Email"
                value={formData.email}
                onChange={handleInputChange}
                required
              />
            </div>
            
            <div className="form-group">
              <input
                type="tel"
                name="phone"
                placeholder="Your Phone"
                value={formData.phone}
                onChange={handleInputChange}
              />
            </div>
            
            <div className="form-group">
              <textarea
                name="message"
                placeholder="Tell us about your project..."
                rows="5"
                value={formData.message}
                onChange={handleInputChange}
                required
              ></textarea>
            </div>
            
            <button type="submit" className="submit-btn">Send Message</button>
          </form> */}
        </div>
      </div>
    </section>
  );
};

// Social links
const SocialLinks = () => (
  <div className="social-links">
    <a
      className="social-link"
      href="https://instagram.com/yourhandle"
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Instagram"
      title="Instagram"
    >
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M7 2C4.243 2 2 4.243 2 7v10c0 2.757 2.243 5 5 5h10c2.757 0 5-2.243 5-5V7c0-2.757-2.243-5-5-5H7zm0 2h10c1.654 0 3 1.346 3 3v10c0 1.654-1.346 3-3 3H7c-1.654 0-3-1.346-3-3V7c0-1.654 1.346-3 3-3zm5 3a5 5 0 100 10 5 5 0 000-10zm0 2a3 3 0 110 6 3 3 0 010-6zm6.5-.75a1.25 1.25 0 11-2.5 0 1.25 1.25 0 012.5 0z" />
      </svg>
    </a>
    <a
      className="social-link"
      href="https://facebook.com/yourpage"
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Facebook"
      title="Facebook"
    >
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M22 12.07C22 6.48 17.52 2 11.93 2S1.86 6.48 1.86 12.07c0 5.02 3.66 9.19 8.44 9.93v-7.02H7.9v-2.91h2.4V9.83c0-2.37 1.42-3.68 3.6-3.68 1.04 0 2.13.19 2.13.19v2.34h-1.2c-1.18 0-1.55.73-1.55 1.47v1.77h2.64l-.42 2.91h-2.22v7.02c4.78-.74 8.44-4.91 8.44-9.93z" />
      </svg>
    </a>
  </div>
);

// Footer
const Footer = () => (
  <footer className="footer">
    <div className="container">
      <div className="footer-content">
        <div className="footer-logo">
          <Logo />
        </div>
        <SocialLinks />
        <p>&copy; 2025 The Golden Toolbelt. All rights reserved.</p>
        <p>Licensed • Insured • Professional</p>
      </div>
    </div>
  </footer>
);

// Main App component
function App() {
  useEffect(() => {
    // Smooth scrolling for navigation
    const handleScroll = () => {
      const nav = document.querySelector('.navigation');
      if (window.scrollY > 100) {
        nav.classList.add('nav-scrolled');
      } else {
        nav.classList.remove('nav-scrolled');
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="App">
      <Navigation />
      <Hero />
      <About />
      <HandymanServices />
      <CustomFabricationServices />
      <OurWork />
      <Contact />
      <Footer />
    </div>
  );
}

export default App; 